import React, { useState } from 'react';
import logoText from '@assets/logo.png';
import { Card } from '@components/atoms/Card';
import { Button } from '@components/atoms/Button';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { logoutUser, setUserDetails } from '@redux/reducers/user.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { toast } from 'react-toastify';
import { RootState } from '@redux/store';
import CreateOrganizationModal from '@components/organisms/CreateOrganizationModal';
import { userService } from '@services/user';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from '@hooks/useTranslation';

const NoOrganization: React.FC = () => {
  // Sprint 28, story-143. Bucket 2 — see docs/architecture.md "## Sprint 28" Decision 4.
  const tOrgLifecycle = useTranslation('orgLifecycle');
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { email, firstName, lastName } = useAppSelector(
    (state: RootState) => state.user,
  );
  const [isChecking, setIsChecking] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logoutUser());
    history.push('/login');
  };

  const handleCheckForOrganization = async () => {
    setIsChecking(true);
    try {
      const { user, organization } =
        await userService.getSelfWithOrganization();

      if (organization) {
        dispatch(setUserDetails(user));
        dispatch(setOrganizationData(organization));
        toast.success(tOrgLifecycle.organizationFoundRedirecting);
        history.push('/');
      } else {
        toast.info(tOrgLifecycle.noOrganizationFoundYet);
      }
    } catch (error) {
      toast.error(tOrgLifecycle.failedToCheckOrganization);
    } finally {
      setIsChecking(false);
    }
  };

  const handleOrganizationCreated = async () => {
    try {
      const { user, organization } =
        await userService.getSelfWithOrganization();

      if (organization) {
        dispatch(setUserDetails(user));
        dispatch(setOrganizationData(organization));
        toast.success(tOrgLifecycle.organizationCreatedRedirecting);
        history.push('/');
      } else {
        toast.error(tOrgLifecycle.organizationCreatedNotAssigned);
      }
    } catch (error) {
      toast.error(tOrgLifecycle.failedToLoadOrganization);
    }
  };

  return (
    <div className="flex flex-col gap-10 items-center justify-center min-h-screen bg-indigo-950 dark:bg-neutral-900 p-4">
      <img
        src={logoText}
        alt="Zenhammer Logo"
        className="max-w-sm w-full mb-4 md:mb-0 md:mr-6"
      />

      <Card className="max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex flex-col items-center">
          {/* Building Icon */}
          <div className="w-24 h-24 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-6">
            <BusinessIcon
              sx={{ fontSize: 48 }}
              className="text-gray-600 dark:text-gray-300"
            />
          </div>

          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2 text-center">
            {tOrgLifecycle.noOrganizationTitle}
          </h1>

          <p className="text-gray-700 dark:text-gray-300 text-center mb-2">
            {tOrgLifecycle.noOrganizationMessage}
          </p>

          <p className="text-gray-600 dark:text-gray-400 text-sm text-center mb-6">
            {tOrgLifecycle.noOrganizationHint}
          </p>

          {/* Action Buttons */}
          <div className="w-full flex flex-col gap-3">
            <Button
              variant="primary"
              fullWidth
              onClick={() => setIsModalOpen(true)}
              leftIcon={<AddBusinessIcon fontSize="small" />}>
              {tOrgLifecycle.createAnOrganization}
            </Button>

            <Button
              variant="secondary"
              fullWidth
              onClick={handleCheckForOrganization}
              disabled={isChecking}
              leftIcon={
                <RefreshIcon
                  fontSize="small"
                  className={isChecking ? 'animate-spin' : ''}
                />
              }>
              {isChecking ? tOrgLifecycle.checking : tOrgLifecycle.checkForOrganization}
            </Button>

            <Button
              variant="tertiary"
              fullWidth
              onClick={handleLogout}
              leftIcon={<LogoutIcon fontSize="small" />}>
              {tOrgLifecycle.logout}
            </Button>
          </div>

          {/* Info message */}
          <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg flex gap-3">
            <InfoOutlinedIcon
              className="text-gray-500 dark:text-gray-400 flex-shrink-0"
              fontSize="small"
            />
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {tOrgLifecycle.expectingInvitationHint}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {tOrgLifecycle.pullToRefreshHint}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Create Organization Modal with Payment */}
      <CreateOrganizationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleOrganizationCreated}
        userData={{
          firstName: firstName || '',
          lastName: lastName || '',
          email: email || '',
        }}
      />
    </div>
  );
};

export default NoOrganization;
